import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  List,
  ListSubheader,
  ListItem,
  CircularProgress,
} from '@mui/material';
import { dashboardsApi, metricsApi } from '../services/api';
import { MetricWithValue } from '../types';
import { Dashboard } from '../types';

interface EditCustomDashboardDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  dashboard: Dashboard | null;
}

const EditCustomDashboardDialog: React.FC<EditCustomDashboardDialogProps> = ({
  open,
  onClose,
  onSuccess,
  dashboard,
}) => {
  const [name, setName] = useState('');
  const [metrics, setMetrics] = useState<MetricWithValue[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (open && dashboard) {
      setName(dashboard.name);
      setSelectedIds(new Set());
      setLoadError(null);
      const load = async () => {
        setLoading(true);
        try {
          const [metricList, { metricIds }] = await Promise.all([
            metricsApi.getAll(),
            dashboardsApi.getMetrics(dashboard.id),
          ]);
          setMetrics(metricList);
          const canonicalByKey = new Map<string, MetricWithValue>();
          metricList.forEach((m) => {
            const key = `${m.name}\n${m.department_name ?? ''}`;
            const existing = canonicalByKey.get(key);
            if (!existing || m.id < existing.id) canonicalByKey.set(key, m);
          });
          const canonicalIds = new Set(metricIds.map((id) => {
            const m = metricList.find((x) => x.id === id);
            if (!m) return id;
            const key = `${m.name}\n${m.department_name ?? ''}`;
            return canonicalByKey.get(key)?.id ?? id;
          }));
          setSelectedIds(canonicalIds);
        } catch (e: any) {
          setLoadError(e.message || 'Failed to load dashboard');
        } finally {
          setLoading(false);
        }
      };
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on open or dashboard id change only
  }, [open, dashboard?.id]);

  const canonicalByKey = new Map<string, MetricWithValue>();
  metrics.forEach((m) => {
    const key = `${m.name}\n${m.department_name ?? ''}`;
    const existing = canonicalByKey.get(key);
    if (!existing || m.id < existing.id) canonicalByKey.set(key, m);
  });
  const displayMetrics = Array.from(canonicalByKey.values());
  const byDepartment = displayMetrics.reduce<Record<string, MetricWithValue[]>>((acc, m) => {
    const key = m.department_name || 'No department';
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});

  const handleToggle = (metricId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(metricId)) next.delete(metricId);
      else next.add(metricId);
      return next;
    });
  };

  const handleSelectAllInDepartment = (departmentName: string | null) => {
    const inDept = byDepartment[departmentName ?? 'No department'] ?? [];
    const allSelected = inDept.every((m) => selectedIds.has(m.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      inDept.forEach((m) => {
        if (allSelected) next.delete(m.id);
        else next.add(m.id);
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!dashboard) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const metricIds = Array.from(selectedIds);
    if (metricIds.length === 0) return; // do not save empty metric list
    if (loading) return; // do not save while still loading current metrics

    setSaving(true);
    try {
      await dashboardsApi.update(dashboard.id, { name: trimmed });
      await dashboardsApi.setMetrics(dashboard.id, metricIds);
      onSuccess();
      onClose();
    } catch (e: any) {
      setLoadError(e.message || 'Failed to save dashboard');
    } finally {
      setSaving(false);
    }
  };

  const departmentNames = Object.keys(byDepartment).sort();

  if (!dashboard) return null;

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit custom dashboard</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Dashboard name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sales + Finance view"
            fullWidth
            autoFocus
          />
          <Typography variant="subtitle2" color="text.secondary">
            Add or remove metrics for this table view. They keep their quarterly targets from their primary boards.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : loadError ? (
            <Typography color="error">{loadError}</Typography>
          ) : (
            <List
              dense
              sx={{
                maxHeight: 360,
                overflow: 'auto',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {departmentNames.map((deptName) => (
                <React.Fragment key={deptName}>
                  <ListSubheader
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: 'background.paper',
                      py: 0.5,
                    }}
                  >
                    <FormControlLabel
                      label={deptName}
                      control={
                        <Checkbox
                          size="small"
                          checked={byDepartment[deptName].every((m) => selectedIds.has(m.id))}
                          indeterminate={
                            byDepartment[deptName].some((m) => selectedIds.has(m.id)) &&
                            !byDepartment[deptName].every((m) => selectedIds.has(m.id))
                          }
                          onChange={() => handleSelectAllInDepartment(deptName)}
                        />
                      }
                    />
                  </ListSubheader>
                  {byDepartment[deptName].map((m) => (
                    <ListItem key={m.id} dense disablePadding sx={{ pl: 4 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedIds.has(m.id)}
                            onChange={() => handleToggle(m.id)}
                          />
                        }
                        label={m.name}
                      />
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name.trim() || selectedIds.size === 0 || loading}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCustomDashboardDialog;
